import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TablePagination from '@material-ui/core/TablePagination';
import TableRow from '@material-ui/core/TableRow';
import TableSortLabel from '@material-ui/core/TableSortLabel';
import Paper from '@material-ui/core/Paper';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import CircularProgress from '@material-ui/core/CircularProgress';

import moment from 'moment';

function desc(a, b, orderBy) {
	if (b[orderBy] < a[orderBy]) {
		return -1;
	}
	if (b[orderBy] > a[orderBy]) {
		return 1;
	}
	return 0;
}

function stableSort(array, cmp) {
	const stabilizedThis = array.map((el, index) => [el, index]);
	stabilizedThis.sort((a, b) => {
		const order = cmp(a[0], b[0]);
		if (order !== 0) return order;
		return a[1] - b[1];
	});
	return stabilizedThis.map(el => el[0]);
}

function getSorting(order, orderBy) {
	return order === 'desc'
		? (a, b) => desc(a, b, orderBy)
		: (a, b) => -desc(a, b, orderBy);
}

function getGroupAwareSorting(order, orderBy, groupBy) {
	return (a, b) => {
		// First, sort by group columns to ensure groups are contiguous
		if (groupBy) {
			const groupColumns = typeof groupBy === 'string' ? [groupBy] : groupBy;
			
			for (let groupCol of groupColumns) {
				const groupComparison = desc(a, b, groupCol);
				if (groupComparison !== 0) {
					return -groupComparison; // Always use ascending for group columns to keep groups together
				}
			}
		}
		
		// Within the same group, sort by the user's selected criteria
		// But skip if user is sorting by the same column we're already grouping by
		const isGroupColumn = groupBy && (
			typeof groupBy === 'string' ? orderBy === groupBy : groupBy.includes(orderBy)
		);
		
		if (!isGroupColumn) {
			const userSortComparison = desc(a, b, orderBy);
			return order === 'desc' ? userSortComparison : -userSortComparison;
		}
		
		return 0;
	};
}

function formatCellValue(columnProps, row) {
	if (!row[columnProps.id]) return columnProps.default ?? 'N/A';
	if (columnProps.isDate) {
		const date = columnProps.dateOptions && columnProps.dateOptions.isUTC ?
			moment(row[columnProps.id]).utc(columnProps.dateOptions.convertUTCToLocalTimeZone) : moment(row[columnProps.id]);
		return date.format('MM/DD/YYYY');
	} else {
		return row[columnProps.id];
	}
}

function groupRowsByColumn(rows, groupBy) {
	if (!groupBy || !rows || rows.length === 0) return [];
	
	const groups = [];
	const groupMap = new Map();
	
	rows.forEach((row, index) => {
		const groupKey = typeof groupBy === 'string' ? 
			row[groupBy] : 
			groupBy.map(col => row[col]).join('|');
		
		if (!groupMap.has(groupKey)) {
			const group = {
				key: groupKey,
				rows: [],
				firstRowIndex: index,
				rowSpan: 0
			};
			groupMap.set(groupKey, group);
			groups.push(group);
		}
		
		groupMap.get(groupKey).rows.push(row);
		groupMap.get(groupKey).rowSpan++;
	});
	
	return groups;
}

function createGroupedRowsWithSpanInfo(rows, groupBy) {
	if (!groupBy || !rows || rows.length === 0) {
		return rows.map(row => ({ ...row, _isGrouped: false }));
	}
	
	const groups = groupRowsByColumn(rows, groupBy);
	const groupedRows = [];
	
	groups.forEach(group => {
		group.rows.forEach((row, rowIndex) => {
			const isFirstInGroup = rowIndex === 0;
			groupedRows.push({
				...row,
				_isGrouped: true,
				_isFirstInGroup: isFirstInGroup,
				_groupKey: group.key,
				_groupRows: group.rows,
				_rowSpan: group.rowSpan
			});
		});
	});
	
	return groupedRows;
}

function adjustGroupingForPagination(rows, groupBy, startIndex, endIndex) {
	if (!groupBy || !rows || rows.length === 0) {
		return rows.map(row => ({ ...row, _isGrouped: false }));
	}
	
	// Get the paginated slice
	const paginatedRows = rows.slice(startIndex, endIndex);
	const adjustedRows = [];
	
	paginatedRows.forEach((row, pageIndex) => {
		// Find all rows with the same group key in the original dataset
		const groupKey = typeof groupBy === 'string' ? 
			row[groupBy] : 
			groupBy.map(col => row[col]).join('|');
		
		const allGroupRows = rows.filter(r => {
			const rGroupKey = typeof groupBy === 'string' ? 
				r[groupBy] : 
				groupBy.map(col => r[col]).join('|');
			return rGroupKey === groupKey;
		});
		
		// Find the first occurrence of this group in the current page
		const firstInPageIndex = paginatedRows.findIndex(r => {
			const rGroupKey = typeof groupBy === 'string' ? 
				r[groupBy] : 
				groupBy.map(col => r[col]).join('|');
			return rGroupKey === groupKey;
		});
		
		const isFirstInPage = pageIndex === firstInPageIndex;
		
		// Calculate how many rows of this group are on the current page
		const groupRowsInPage = paginatedRows.filter(r => {
			const rGroupKey = typeof groupBy === 'string' ? 
				r[groupBy] : 
				groupBy.map(col => r[col]).join('|');
			return rGroupKey === groupKey;
		});
		
		adjustedRows.push({
			...row,
			_isGrouped: true,
			_isFirstInGroup: isFirstInPage,
			_groupKey: groupKey,
			_groupRows: allGroupRows, // All rows in the group across all pages
			_groupRowsInPage: groupRowsInPage, // Only rows in current page
			_rowSpan: groupRowsInPage.length, // Span only for current page
			_totalGroupSize: allGroupRows.length // Total group size across all pages
		});
	});
	
	return adjustedRows;
}

function changeRow(rows, row) {
	const newRows = [];
	for (let i = 0; i < rows.length; i++) {
		if (i === row.index) {
			newRows.push(row);
		} else {
			newRows.push(rows.find(r => r.index === i));
		}
	}
	return newRows;
}

function ActionComponent(props) {
	const { component, row, rows, setRows } = props;
	const Component = component.type;
	const setRow = row => {
		setRows(changeRow(rows, row));
	};

	return (
		<Component
			{...component.props}
			row={row}
			setRow={setRow}
			rows={rows}
			setRows={setRows}
		/>
	);
}

ActionComponent.propTypes = {
	component: PropTypes.shape({
		type: PropTypes.elementType.isRequired,
		props: PropTypes.object
	}).isRequired,
	row: PropTypes.object.isRequired,
	rows: PropTypes.array.isRequired,
	setRows: PropTypes.func.isRequired
};

function GroupedCellComponent(props) {
	const { component, row, rows, setRows } = props;
	const Component = component.type;
	
	return (
		<Component
			{...component.props}
			groupKey={row._groupKey}
			groupRows={row._groupRows}
			groupRowsInPage={row._groupRowsInPage}
			totalGroupSize={row._totalGroupSize}
			isPartialGroup={row._groupRowsInPage && row._groupRowsInPage.length !== row._totalGroupSize}
			row={row}
			rows={rows}
			setRows={setRows}
		/>
	);
}

GroupedCellComponent.propTypes = {
	component: PropTypes.shape({
		type: PropTypes.elementType.isRequired,
		props: PropTypes.object
	}).isRequired,
	row: PropTypes.shape({
		_groupKey: PropTypes.string,
		_groupRows: PropTypes.array,
		_groupRowsInPage: PropTypes.array,
		_totalGroupSize: PropTypes.number
	}).isRequired,
	rows: PropTypes.array.isRequired,
	setRows: PropTypes.func.isRequired
};

function EnhancedTableHead(props) {
	const { classes, order, orderBy, onRequestSort, headCells } = props;

	const createSortHandler = property => event => {
		onRequestSort(event, property);
	};

	return (
		<TableHead>
			<TableRow>
				{headCells.map(headCell => (
					<TableCell
						className={classes.tableHeaderCell}
						key={headCell.id}
						sortDirection={orderBy === headCell.id ? order : false}
						style={{ width: headCell.width || 'inherit' }}
					>
						<TableSortLabel
							active={orderBy === headCell.id}
							direction={orderBy === headCell.id ? order : 'asc'}
							onClick={createSortHandler(headCell.id)}
							IconComponent={ArrowDropDownIcon}
							classes={{
								root: classes.sortLabel,
								icon: [
									classes.sortIcon,
									orderBy === headCell.id ? {} : classes.inActiveSortIcon
								].join(' ')
							}}
						>
							{headCell.label}
							{orderBy === headCell.id ? (
								<span className={classes.visuallyHidden}>
									{order === 'desc' ? 'sorted descending' : 'sorted ascending'}
								</span>
							) : null}
						</TableSortLabel>
					</TableCell>
				))}
			</TableRow>
		</TableHead>
	);
}

EnhancedTableHead.propTypes = {
	classes: PropTypes.object.isRequired,
	onRequestSort: PropTypes.func.isRequired,
	order: PropTypes.oneOf(['asc', 'desc']).isRequired,
	orderBy: PropTypes.string.isRequired,
	headCells: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.string.isRequired,
			label: PropTypes.string.isRequired,
			width: PropTypes.string,
			isDate: PropTypes.bool,
			default: PropTypes.string,
			dateOptions: PropTypes.shape({
				isUTC: PropTypes.bool,
				convertUTCToLocalTimeZone: PropTypes.bool
			})
		})
	).isRequired
};

const useStyles = makeStyles(theme => ({
	root: {
		width: '100%',
		position: 'relative'
	},
	paper: {
		width: '100%',
		marginBottom: theme.spacing(2)
	},
	table: {
		minWidth: 650
	},
	visuallyHidden: {
		border: 0,
		clip: 'rect(0 0 0 0)',
		height: 1,
		margin: -1,
		overflow: 'hidden',
		padding: 0,
		position: 'absolute',
		top: 20,
		width: 1
	},
	tableHeaderCell: {
		fontSize: 15,
		fontFamily: 'Lato,"Helvetica Neue",Helvetica,Arial,sans-serif',
		color: '#2c3e50',
		textAlign: 'left',
		fontWeight: '700',
		padding: theme.spacing(1),
		border: '1px solid #ecf0f1'
	},
	odd: {
		backgroundColor: '#f9f9f9',
		'&:hover': {
			backgroundColor: '#ecf0f1'
		}
	},
	even: {
		backgroundColor: '#0000',
		'&:hover': {
			backgroundColor: '#ecf0f1'
		}
	},
	// Row classes for when grouping is enabled (no row-level hover)
	oddGrouped: {
		backgroundColor: '#f9f9f9'
	},
	evenGrouped: {
		backgroundColor: '#0000'
	},
	cellBody: {
		border: '1px solid #ecf0f1',
		color: '#2c3e50',
		padding: theme.spacing(1)
	},
	// Cell that should highlight when row is hovered (grouping enabled)
	hoveredCell: {
		border: '1px solid #ecf0f1',
		color: '#2c3e50',
		padding: theme.spacing(1),
		backgroundColor: '#ecf0f1'
	},
	// Grouped cell that should never highlight and has neutral background
	groupedCell: {
		border: '1px solid #ecf0f1',
		color: '#2c3e50',
		padding: theme.spacing(1),
		backgroundColor: '#ffffff !important' // Override any inherited row colors
	},
	link: {
		textDecoration: 'none'
	},
	sortLabel: {
		width: '100%'
	},
	sortIcon: {
		marginLeft: 'auto'
	},
	inActiveSortIcon: {
		opacity: '0.3'
	},
	loadingPanel: {
		position: 'absolute',
		top: 0,
		left: 0,
		width: '100%',
		height: '100%',
		backgroundColor: 'rgba(255, 255, 255, 0.5);',
		zIndex: 1,
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		'& > *': {
			display: 'flex',
			flexDirection: 'column',
			alignItems: 'center',
			backgroundColor: '#fff',
			borderRadius: '0 0 4px 4px',
			margin: '1px',
			padding: theme.spacing(1),
			width: 150
		}
	},
	noDataAvailableRow : {
		fontSize : '15px',
		backgroundColor: '#f9f9f9'
	}
}));

export default function EnhancedTable(props) {
	const {
		rows, // the data source to populate the table.
		customCells, // custom action components to be rendered.
		/* 
			You must use the correct column id as the name of the custom cell for it to be rendered.
		*/
		headCells, // header cells properties.
		rowsPerPageOptions, // options to be displayed in rows per page dropdown.
		useDbPagination, // 'true' to use database pagination.
		onPageChange, // callback function executed on page change.
		showPagination, // 'true' to show pagination section.
		pageCount, // the count of rows diplayed per page.
		isLoading,
		fetchRowsPromise, // A promise that takes the pageNo as a parameter and returns an object {rows: collection of rows, rowCount: total rows count} if it succeeds
		rowKey, // the key to be used for the row
		groupBy, // string or array of column ids to group by
		groupRenderers, // object with column id as key and renderer component as value for grouped cells
		enableGrouping = false // boolean to enable/disable grouping
	} = props;

	const classes = useStyles();
	const [order, setOrder] = React.useState('asc');
	const [orderBy, setOrderBy] = React.useState(headCells[0].id);
	const [page, setPage] = React.useState(0);
	const [rowsPerPage, setRowsPerPage] = React.useState(
		showPagination
			? rowsPerPageOptions && rowsPerPageOptions.length
				? rowsPerPageOptions[0]
				: pageCount || 20
			: 20
	);
	const [dataRows, setDataRows] = useState([]);
	const [rowCount, setRowCount] = useState(0);
	const [isPromiseLoading, setIsPromiseLoading] = useState(false);
	const [hoveredRowIndex, setHoveredRowIndex] = useState(null);
	const isInitialRender = React.useRef(true);

	const handleRequestSort = (event, property) => {
		const isAsc = orderBy === property && order === 'asc';
		setOrder(isAsc ? 'desc' : 'asc');
		setOrderBy(property);
	};

	const handleChangePage = (event, newPage) => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = event => {
		setRowsPerPage(parseInt(event.target.value, 10));
		setPage(0);
	};

	const emptyRows =
		rowsPerPage -
		Math.min(
			rowsPerPage,
			(useDbPagination ? rowCount : dataRows.length) - page * rowsPerPage
		);

	// Asynchronous Fetch
	const fetchPromiseData = async (page) => {
		if (!fetchRowsPromise) return;

		setIsPromiseLoading(true);
		try {
			const result = await fetchRowsPromise(page * rowsPerPage, rowsPerPage);
			const { rows, rowCount } = result;
			// adding index column to reserve original ordered index
			const indexedRows = rows.map((row, index) => ({ ...row, index }));
			const processedRows = enableGrouping ? 
				createGroupedRowsWithSpanInfo(indexedRows, groupBy) : 
				indexedRows;
			setDataRows(processedRows);
			setRowCount(rowCount);
			setIsPromiseLoading(false);
		} catch (err) {
			setIsPromiseLoading(false);
			console.error(err);
		}
	};

	useEffect(() => {
		if (useDbPagination) {
			if (page > 0) {
				setPage(0);
			} else {
				fetchPromiseData(0);
			}
		} else {
			fetchPromiseData();
		}
	}, [fetchRowsPromise]);
	// End of Async Fetch

	// Synchronous Fetch
	useEffect(() => {
		if (rows) {
			// adding index column to reserve original ordered index
			const indexedRows = rows.map((row, index) => ({ ...row, index }));
			const processedRows = enableGrouping ? 
				createGroupedRowsWithSpanInfo(indexedRows, groupBy) : 
				indexedRows;
			setDataRows(processedRows);
		}
	}, [rows, enableGrouping, groupBy]);
	// End of Synchronous Fetch

	useEffect(() => {
		if (useDbPagination) {
			if (isInitialRender.current) {
				isInitialRender.current = false;
				return;
			}
			fetchPromiseData(page);
		}

		if (onPageChange) {
			onPageChange(page);
		}
	}, [page]);

	return (
        <div className={classes.root}>
            {(isLoading || isPromiseLoading) && (
                <div className={classes.loadingPanel}>
                    <Paper>
                        <CircularProgress color='inherit' />
                    </Paper>
                </div>
            )}
            <Paper className={classes.paper}>
                <TableContainer>
                    <Table className={classes.table}>
                        <EnhancedTableHead
                            classes={classes}
                            order={order}
                            orderBy={orderBy}
                            onRequestSort={handleRequestSort}
                            headCells={headCells}
                        />
                        {dataRows.length > 0 ? (
                            <TableBody>
                                {(() => {
                                    // Use group-aware sorting when grouping is enabled
                                    const sortingFunction = enableGrouping ? 
                                        getGroupAwareSorting(order, orderBy, groupBy) :
                                        getSorting(order, orderBy);
                                    
                                    const sortedRows = stableSort(dataRows, sortingFunction);
                                    const startIndex = (useDbPagination ? 0 : page) * rowsPerPage;
                                    const endIndex = startIndex + rowsPerPage;
                                    
                                    // Apply pagination-aware grouping if grouping is enabled
                                    const displayRows = enableGrouping && showPagination && !useDbPagination ? 
                                        adjustGroupingForPagination(sortedRows, groupBy, startIndex, endIndex) :
                                        sortedRows.slice(startIndex, endIndex);
                                    
                                    // Helper function to determine if a column should be grouped
                                    const shouldColumnBeGrouped = (col) => {
                                        return enableGrouping && 
                                            groupBy && 
                                            (typeof groupBy === 'string' ? 
                                                col.id === groupBy : 
                                                groupBy.includes(col.id));
                                    };

                                    // Helper function to get cell class
                                    const getCellClass = (shouldGroupColumn, rowIndex) => {
                                        if (shouldGroupColumn) {
                                            return classes.groupedCell;
                                        }
                                        if (enableGrouping && hoveredRowIndex === rowIndex) {
                                            return classes.hoveredCell;
                                        }
                                        return classes.cellBody;
                                    };

                                    // Helper function to get cell title
                                    const getCellTitle = (col, row, hasCustomCell, hasGroupRenderer) => {
                                        return hasCustomCell || hasGroupRenderer ? '' : formatCellValue(col, row);
                                    };

                                    // Helper function to get mouse event handlers
                                    const getMouseHandlers = (shouldGroupColumn) => {
                                        if (!shouldGroupColumn || !enableGrouping) {
                                            return { onMouseEnter: undefined, onMouseLeave: undefined };
                                        }
                                        
                                        return {
                                            onMouseEnter: (e) => {
                                                e.stopPropagation();
                                                setHoveredRowIndex(null);
                                            },
                                            onMouseLeave: (e) => {
                                                e.stopPropagation();
                                            }
                                        };
                                    };

                                    // Helper function to render cell content
                                    const renderCellContent = (hasGroupRenderer, col, customCell, row) => {
                                        if (hasGroupRenderer) {
                                            return (
                                                <GroupedCellComponent
                                                    component={groupRenderers[col.id]}
                                                    row={row}
                                                    rows={dataRows}
                                                    setRows={setDataRows}
                                                />
                                            );
                                        }
                                        
                                        if (customCell) {
                                            return (
                                                <ActionComponent
                                                    component={customCell}
                                                    row={row}
                                                    rows={dataRows}
                                                    setRows={setDataRows}
                                                />
                                            );
                                        }
                                        
                                        return formatCellValue(col, row);
                                    };

                                    return displayRows.map((row, index) => {
                                        // Use different row classes based on grouping state
                                        const rowClass = enableGrouping ? 
                                            ((index + 1) % 2 === 0 ? classes.evenGrouped : classes.oddGrouped) :
                                            ((index + 1) % 2 === 0 ? classes.even : classes.odd);
                                        
                                        return (
                                            <TableRow
                                                className={rowClass}
                                                tabIndex={-1}
                                                key={rowKey ? row[rowKey] : row.index || index}
                                                onMouseEnter={enableGrouping ? () => setHoveredRowIndex(index) : undefined}
                                                onMouseLeave={enableGrouping ? () => setHoveredRowIndex(null) : undefined}
                                            >
                                                {headCells.map((col) => {
                                                    const shouldGroupColumn = shouldColumnBeGrouped(col);
                                                    
                                                    // Skip rendering grouped cells for non-first rows in group
                                                    if (shouldGroupColumn && row._isGrouped && !row._isFirstInGroup) {
                                                        return null;
                                                    }
                                                    
                                                    // Calculate cell properties
                                                    const rowSpan = shouldGroupColumn && row._isGrouped && row._isFirstInGroup ? row._rowSpan : 1;
                                                    const hasGroupRenderer = shouldGroupColumn && groupRenderers?.[col.id];
                                                    const customCell = customCells?.[col.id];
                                                    const mouseHandlers = getMouseHandlers(shouldGroupColumn);
                                                    
                                                    return (
                                                        <TableCell
                                                            key={col.id + index}
                                                            rowSpan={rowSpan}
                                                            title={getCellTitle(col, row, customCell, hasGroupRenderer)}
                                                            className={getCellClass(shouldGroupColumn, index)}
                                                            onMouseEnter={mouseHandlers.onMouseEnter}
                                                            onMouseLeave={mouseHandlers.onMouseLeave}
                                                        >
                                                            {renderCellContent(hasGroupRenderer, col, customCell, row)}
                                                        </TableCell>
                                                    );
                                                })}
                                            </TableRow>
                                        );
                                    });
                                })()}
                                {emptyRows > 0 && (
                                    <TableRow>
                                        <TableCell colSpan={headCells.length} />
                                    </TableRow>
                                )}
                            </TableBody>
                        ) : (
                            <TableBody>
                                <TableRow>
                                    <TableCell
                                        colSpan={headCells.length}
                                        className={classes.noDataAvailableRow}
                                    >
                                        No data available in table
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        )}
                    </Table>
                </TableContainer>
                {showPagination && (
                    <TablePagination
                        rowsPerPageOptions={rowsPerPageOptions || []}
                        component='div'
                        count={useDbPagination ? rowCount : rows?.length ?? 0}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                    />
                )}
            </Paper>
        </div>
    );
}

EnhancedTable.propTypes = {
	rows: PropTypes.array,
	customCells: PropTypes.objectOf(PropTypes.element),
	headCells: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.string.isRequired,
			label: PropTypes.string.isRequired,
			width: PropTypes.string,
			isDate: PropTypes.bool,
			default: PropTypes.string,
			dateOptions: PropTypes.shape({
				isUTC: PropTypes.bool,
				convertUTCToLocalTimeZone: PropTypes.bool
			})
		})
	).isRequired,
	rowsPerPageOptions: PropTypes.array,
	useDbPagination: PropTypes.bool,
	onPageChange: PropTypes.func,
	showPagination: PropTypes.bool,
	pageCount: PropTypes.number,
	isLoading: PropTypes.bool,
	fetchRowsPromise: PropTypes.func,
	rowKey: PropTypes.string,
	groupBy: PropTypes.oneOfType([
		PropTypes.string,
		PropTypes.arrayOf(PropTypes.string)
	]),
	groupRenderers: PropTypes.objectOf(
		PropTypes.shape({
			type: PropTypes.elementType.isRequired,
			props: PropTypes.object
		})
	),
	enableGrouping: PropTypes.bool
};

EnhancedTable.defaultProps = {
	rows: [],
	customCells: {},
	rowsPerPageOptions: [5, 10, 25],
	useDbPagination: false,
	onPageChange: null,
	showPagination: true,
	pageCount: 5,
	isLoading: false,
	fetchRowsPromise: null,
	rowKey: 'id',
	groupBy: null,
	groupRenderers: {},
	enableGrouping: false
};
